import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Закрепление представления: где показывать и кому видно.
//
// Мест два и они независимы — пункт бокового меню и плитка на дашборде, — так
// что это два флажка, а не выбор одного из двух. Доступ по умолчанию личный:
// представление собирают под себя, и делиться им человек решает отдельно.
import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Radio, Select, Space } from 'antd';

import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import type { SaveViewPayload, SavedView } from '../api/views.client.js';
import type { ViewSpec } from '../../../views/spec.js';

type Access = 'private' | 'selected' | 'everyone';

interface SaveViewModalProps {
  open: boolean;
  spec: ViewSpec;
  /** Задано при правке уже закреплённого представления. */
  existing?: SavedView | undefined;
  saving?: boolean;
  error?: unknown;
  onCancel: () => void;
  onSubmit: (payload: SaveViewPayload) => void;
}

function accessOf(view: SavedView | undefined): Access {
  if (view === undefined) return 'private';
  if (view.sharedToAll) return 'everyone';
  return view.sharedWithMemberIds.length > 0 ? 'selected' : 'private';
}

export function SaveViewModal({
  open,
  spec,
  existing,
  saving = false,
  error,
  onCancel,
  onSubmit,
}: SaveViewModalProps): ReactElement {
  useI18nSubscription();
  const members = useMembers();
  const [title, setTitle] = useState(existing?.title ?? spec.title);
  const [inSidebar, setInSidebar] = useState(existing?.inSidebar ?? true);
  const [onDashboard, setOnDashboard] = useState(existing?.onDashboard ?? false);
  const [access, setAccess] = useState<Access>(accessOf(existing));
  const [memberIds, setMemberIds] = useState<string[]>(existing?.sharedWithMemberIds ?? []);

  // Модалка остаётся смонтированной между открытиями — без сброса второе
  // закрепление подставляло бы заголовок от первого.
  useEffect(() => {
    if (!open) return;
    setTitle(existing?.title ?? spec.title);
    setInSidebar(existing?.inSidebar ?? true);
    setOnDashboard(existing?.onDashboard ?? false);
    setAccess(accessOf(existing));
    setMemberIds(existing?.sharedWithMemberIds ?? []);
  }, [open, existing, spec.title]);

  const submit = (): void => {
    onSubmit({
      title: title.trim(),
      spec,
      inSidebar,
      onDashboard,
      sharedToAll: access === 'everyone',
      sharedWithMemberIds: access === 'selected' ? memberIds : [],
    });
  };

  return (
    <Modal
      open={open}
      title={
        existing === undefined
          ? i18n.t('crm.features.features.views.components.saveviewmodal.закрепить_представление')
          : i18n.t('crm.features.features.views.components.saveviewmodal.настройки_представления')
      }
      okText={i18n.t('crm.features.features.views.components.saveviewmodal.сохранить')}
      cancelText={i18n.t('crm.features.features.views.components.saveviewmodal.отмена')}
      confirmLoading={saving}
      okButtonProps={{ disabled: title.trim() === '' || (!inSidebar && !onDashboard) }}
      onOk={submit}
      onCancel={onCancel}
    >
      <Form layout="vertical">
        <Form.Item label={i18n.t('crm.features.features.views.components.saveviewmodal.название')}>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} />
        </Form.Item>

        <Form.Item
          label={i18n.t('crm.features.features.views.components.saveviewmodal.где_показывать')}
        >
          <Space orientation="vertical">
            <Checkbox checked={inSidebar} onChange={(e) => setInSidebar(e.target.checked)}>
              {i18n.t(
                'crm.features.features.views.components.saveviewmodal.пунктом_в_боковом_меню',
              )}
            </Checkbox>
            <Checkbox checked={onDashboard} onChange={(e) => setOnDashboard(e.target.checked)}>
              {i18n.t('crm.features.features.views.components.saveviewmodal.плиткой_на_дашборде')}
            </Checkbox>
          </Space>
        </Form.Item>

        <Form.Item
          label={i18n.t('crm.features.features.views.components.saveviewmodal.кому_видно')}
        >
          <Radio.Group value={access} onChange={(event) => setAccess(event.target.value as Access)}>
            <Space orientation="vertical">
              <Radio value="private">
                {i18n.t('crm.features.features.views.components.saveviewmodal.только_мне')}
              </Radio>
              <Radio value="selected">
                {i18n.t(
                  'crm.features.features.views.components.saveviewmodal.выбранным_участникам',
                )}
              </Radio>
              <Radio value="everyone">
                {i18n.t('crm.features.features.views.components.saveviewmodal.всем_в_пространстве')}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {access === 'selected' ? (
          <Form.Item>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={i18n.t(
                'crm.features.features.views.components.saveviewmodal.кому_открыть_доступ',
              )}
              loading={members.isLoading}
              value={memberIds}
              onChange={setMemberIds}
              optionFilterProp="label"
              options={(members.data ?? []).map((member) => ({
                value: member.id,
                label: `${member.name} (${member.email})`,
              }))}
            />
          </Form.Item>
        ) : null}

        {access === 'private' ? null : (
          // Важно проговорить: доступ к представлению не расширяет доступ к
          // данным. Тот, кому его открыли, увидит только то, что ему и так
          // разрешено, а на остальное получит отказ.
          <Alert
            type="info"
            showIcon
            title={i18n.t(
              'crm.features.features.views.components.saveviewmodal.данные_останутся_под_правами_того_кто_смот',
            )}
            description={i18n.t(
              'crm.features.features.views.components.saveviewmodal.представление_показывает_только_те_записи_',
            )}
          />
        )}

        {error === undefined || error === null ? null : (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 12 }}
            title={i18n.t(
              'crm.features.features.views.components.saveviewmodal.не_удалось_сохранить',
            )}
            description={error instanceof Error ? error.message : String(error)}
          />
        )}
      </Form>
    </Modal>
  );
}
